import { useEffect, useState } from "react";
import { api, API, getToken } from "../lib/api";
import Layout from "../components/Layout";
import { Upload, Link2, X, Trash2, FileText, Image as ImageIcon, Video as VideoIcon, ExternalLink, File } from "lucide-react";
import { toast } from "sonner";

const Files = () => {
  const [items, setItems] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/files").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "all" ? items : items.filter((f) => f.category === filter);

  return (
    <Layout
      title="Archivos & UGC"
      subtitle="Sube o pega URLs externos"
      actions={
        <div className="flex gap-2">
          <button onClick={() => setShowLink(true)} className="btn-outline flex items-center gap-2" data-testid="add-link-btn">
            <Link2 size={14} /> Pegar URL
          </button>
          <button onClick={() => setShowUpload(true)} className="btn-primary flex items-center gap-2" data-testid="upload-btn">
            <Upload size={14} /> Subir archivo
          </button>
        </div>
      }
    >
      <div className="flex gap-2 mb-5 flex-wrap">
        {["all", "design", "video", "ugc", "brand", "reference", "general"].map((c) => (
          <button key={c} onClick={() => setFilter(c)} className={`pill cursor-pointer ${filter === c ? "pill-yellow" : "pill-gray"}`}>
            {c === "all" ? "Todos" : c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <Upload size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin archivos</h3>
          <p className="text-[#666] mt-2 mb-5">Sube videos UGC, diseños, fotos, o pega links externos (Drive, WeTransfer).</p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => setShowLink(true)} className="btn-outline">Pegar URL</button>
            <button onClick={() => setShowUpload(true)} className="btn-primary">Subir primero</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((f) => <FileCard key={f.id} file={f} onChange={load} />)}
        </div>
      )}

      {showUpload && <UploadForm onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
      {showLink && <LinkForm onClose={() => setShowLink(false)} onSaved={() => { setShowLink(false); load(); }} />}
    </Layout>
  );
};

const FileCard = ({ file, onChange }) => {
  const isImage = file.content_type?.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(file.original_filename || "");
  const isVideo = file.content_type?.startsWith("video/") || /\.(mp4|mov|webm)$/i.test(file.original_filename || "");
  const directUrl = file.external_url || `${API}${file.download_url}?auth=${getToken()}`;

  const remove = async () => {
    if (!window.confirm("¿Eliminar archivo?")) return;
    await api.delete(`/files/${file.id}`);
    onChange();
  };

  return (
    <div className="aura-card overflow-hidden group" data-testid={`file-${file.id}`}>
      <div className="aspect-video bg-[#F0F0F0] flex items-center justify-center overflow-hidden">
        {isImage && file.external_url ? (
          <img src={file.external_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : isImage ? (
          <img src={directUrl} alt="" className="w-full h-full object-cover" />
        ) : isVideo ? (
          <VideoIcon size={36} className="text-[#666]" />
        ) : file.external_url ? (
          <Link2 size={36} className="text-[#666]" />
        ) : (
          <File size={36} className="text-[#666]" />
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-medium truncate">{file.original_filename}</div>
        <div className="flex justify-between items-center mt-2">
          <span className="pill pill-gray text-[10px]">{file.category}</span>
          <div className="flex gap-1">
            <a href={directUrl} target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-[#0A0A0A] p-1"><ExternalLink size={13} /></a>
            <button onClick={remove} className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-600 p-1"><Trash2 size={13} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadForm = ({ onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("general");
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Archivo subido");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 space-y-4">
        <div className="flex justify-between items-start"><div><div className="label-overline">Subir</div><h2 className="font-display text-2xl font-medium">Nuevo archivo</h2></div><button type="button" onClick={onClose}><X size={18} /></button></div>
        <div>
          <label className="aura-label">Archivo *</label>
          <input type="file" required onChange={(e) => setFile(e.target.files?.[0])} className="block w-full text-sm" data-testid="file-input" />
          {file && <div className="text-xs text-[#666] mt-2">{file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
        </div>
        <div>
          <label className="aura-label">Categoría</label>
          <select className="aura-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {["general", "design", "video", "ugc", "brand", "reference", "meeting"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button type="submit" disabled={uploading || !file} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="upload-submit">
          {uploading && <span className="spinner" />} Subir archivo
        </button>
      </form>
    </div>
  );
};

const LinkForm = ({ onClose, onSaved }) => {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("reference");

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    await api.post("/files/external", { url, title, category });
    toast.success("Link guardado");
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 space-y-4">
        <div className="flex justify-between items-start"><div><div className="label-overline">URL externa</div><h2 className="font-display text-2xl font-medium">Pegar link</h2></div><button type="button" onClick={onClose}><X size={18} /></button></div>
        <div><label className="aura-label">URL *</label><input required className="aura-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/..." data-testid="link-url" /></div>
        <div><label className="aura-label">Título</label><input className="aura-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Video UGC Tere" /></div>
        <div>
          <label className="aura-label">Categoría</label>
          <select className="aura-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {["general", "design", "video", "ugc", "brand", "reference", "meeting"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button type="submit" className="btn-primary w-full justify-center" data-testid="link-submit">Guardar link</button>
      </form>
    </div>
  );
};

export default Files;
